import { useState } from "react";
import Editor from "@monaco-editor/react";
import api from "@/lib/api";
import { toast } from "sonner";
import EditorShell from "./EditorShell";
import { Save, Copy } from "lucide-react";
import TagsInput from "./TagsInput";

const HTML_STARTER = `<!DOCTYPE html>
<html>
<body style="margin:0;padding:24px;font-family:Arial,sans-serif;background:#f5f5f5;color:#1a1a1a;">
  <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;margin:0 auto;background:#ffffff;padding:32px;border-radius:8px;">
    <tr><td>
      <h1 style="margin:0 0 16px;font-size:24px;color:#111;">Hi {{name}},</h1>
      <p style="margin:0 0 16px;font-size:15px;line-height:1.6;">
        I noticed {{company}} is scaling fast — congrats on the recent growth.
      </p>
      <p style="margin:0 0 24px;font-size:15px;line-height:1.6;">
        We help teams like yours land more outbound replies. Worth a 15-min chat?
      </p>
      <a href="https://cal.com" style="display:inline-block;padding:12px 24px;background:#E23A28;color:#ffffff;text-decoration:none;font-weight:600;border-radius:4px;">
        Book a meeting
      </a>
      <p style="margin:32px 0 0;font-size:13px;color:#666;">— Sent with Coldforge</p>
    </td></tr>
  </table>
</body>
</html>`;

export default function HtmlEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [html, setHtml] = useState(initial?.html || HTML_STARTER);
  const [tags, setTags] = useState(initial?.tags || []);
  const [busy, setBusy] = useState(false);

  const persist = async ({ asNew }) => {
    if (!name.trim()) return toast.error("Template name required");
    if (!subject.trim() || !html.trim()) return toast.error("Subject and HTML required");
    setBusy(true);
    try {
      const payload = { name: asNew ? `${name} (copy)` : name, subject, html, body: html, tags };
      let saved;
      if (initial?.id && !asNew) {
        const { data } = await api.put(`/templates/${initial.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/templates", { ...payload, type: "html" });
        saved = data;
      }
      toast.success(asNew ? "Saved as new template" : "Template saved");
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <EditorShell
      testid="html-editor"
      title={initial?.id ? "Edit HTML Template" : "New HTML Template"}
      subtitle="// editor / raw_html"
      onClose={onClose}
      footer={
        <>
          {initial?.id && (
            <button
              type="button"
              onClick={() => persist({ asNew: true })}
              disabled={busy}
              data-testid="html-editor-save-as-new"
              className="cf-btn-secondary"
            >
              <Copy size={14} /> Save as new
            </button>
          )}
          <button
            type="button"
            onClick={() => persist({ asNew: false })}
            disabled={busy}
            data-testid="html-editor-save"
            className="cf-btn-primary"
          >
            <Save size={14} /> {busy ? "Saving..." : "Save Template"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Template name
          </label>
          <input
            data-testid="html-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="cf-input"
            placeholder="e.g. Newsletter — branded"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Subject
          </label>
          <input
            data-testid="html-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="cf-input"
            placeholder="Quick question, {{name}}"
          />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">
            Tags / folders
          </label>
          <TagsInput value={tags} onChange={setTags} testid="html-tags-input" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
        <div className="bg-[#050505]">
          <div className="px-3 py-2 border-b border-zinc-800 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            // code / html
          </div>
          <div data-testid="html-code-editor">
            <Editor
              height="560px"
              defaultLanguage="html"
              value={html}
              onChange={(v) => setHtml(v ?? "")}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                wordWrap: "on",
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
          </div>
        </div>
        <div className="bg-white">
          <div className="px-3 py-2 border-b border-zinc-800 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-[#050505]">
            // live preview
          </div>
          <iframe
            title="HTML preview"
            srcDoc={html}
            sandbox=""
            className="w-full border-0"
            style={{ height: "560px" }}
            data-testid="html-preview-iframe"
          />
        </div>
      </div>
    </EditorShell>
  );
}
